import { PaginatedResponse } from "../types/PaginatedResponse";
import { api } from "./api/apiSlice";

// Activity types
export interface MemberActivity {
  id: string;
  memberId?: string;
  userId?: string;
  title: string;
  memberDescription?: string;
  adminDescription?: string;
  activityType: string;
  activityMetadata?: Record<string, any>;
  createdAt: string;
}

export interface ActivityFilter {
  memberId?: string;
  userId?: string;
  activityType?: string;
  startDate?: string;
  endDate?: string;
}

export interface GetActivitiesParams {
  page?: number;
  size?: number;
  memberId?: string;
  userId?: string;
  activityType?: string;
}

export interface GetRecentActivitiesParams {
  memberId?: string;
  limit?: number;
}

export const activityApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get paginated activities with filters
    getActivities: builder.query<
      PaginatedResponse<MemberActivity>,
      GetActivitiesParams
    >({
      query: (params) => ({
        url: "activities/",
        params: {
          page: params?.page || 1,
          size: params?.size || 20,
          member_id: params?.memberId,
          user_id: params?.userId,
          activity_type: params?.activityType,
        },
      }),
      providesTags: ["activities"],
    }),

    // Get recent activities
    getRecentActivities: builder.query<
      MemberActivity[],
      GetRecentActivitiesParams
    >({
      query: (params = {} as GetRecentActivitiesParams) => ({
        url: "activities/recent",
        params: {
          member_id: params.memberId,
          limit: params.limit || 10,
        },
      }),
      providesTags: ["activities"],
    }),

    // Get activities for a specific member
    getMemberActivities: builder.query<
      PaginatedResponse<MemberActivity>,
      { memberId: string } & GetActivitiesParams
    >({
      query: ({ memberId, ...params }) => ({
        url: `activities/member/${memberId}`,
        params: {
          page: params?.page || 1,
          size: params?.size || 20,
          activity_type: params?.activityType,
        },
      }),
      providesTags: ["activities"],
    }),

    // Get a specific activity
    getActivity: builder.query<MemberActivity, string>({
      query: (activityId) => `activities/${activityId}`,
      providesTags: (_result, _error, activityId) => [
        { type: "activities", id: activityId },
      ],
    }),

    // Create a new activity (admin only)
    createActivity: builder.mutation<MemberActivity, Partial<MemberActivity>>({
      query: (activityData) => ({
        url: "activities/",
        method: "POST",
        body: activityData,
      }),
      invalidatesTags: ["activities"],
    }),

    // Update an activity (admin only)
    updateActivity: builder.mutation<
      MemberActivity,
      { id: string; data: Partial<MemberActivity> }
    >({
      query: ({ id, data }) => ({
        url: `activities/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["activities"],
    }),

    // Delete an activity (admin only)
    deleteActivity: builder.mutation<{ message: string }, string>({
      query: (activityId) => ({
        url: `activities/${activityId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["activities"],
    }),
  }),
});

export const {
  useGetActivitiesQuery,
  useGetRecentActivitiesQuery,
  useGetMemberActivitiesQuery,
  useGetActivityQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
} = activityApi;
